import React, { useState } from 'react';
import { Sliders, Sun, Moon, Save, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export const SettingsPage: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [confidenceThreshold, setConfidenceThreshold] = useState(65);
  const [densityLimit, setDensityLimit] = useState(85);
  const [hardwareAcc, setHardwareAcc] = useState(true);
  const [soundAlerts, setSoundAlerts] = useState(true);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs">
        <div>
          <h1 className="text-base font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <Sliders className="w-5 h-5 text-orange-500" />
            System & AI Configuration
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Tune computer vision parameters, theme preferences, and safety density triggers
          </p>
        </div>

        <button 
          onClick={handleSave}
          className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium text-xs shadow-sm flex items-center gap-1.5 cursor-pointer transition-colors"
        >
          {saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
          {saved ? 'Settings Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Theme Appearance Card */}
      <div className="p-4 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          Interface Theme & Appearance
        </h3>
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          Select between clean Light mode and sleek high-contrast Darker-Grey mode with orange highlights.
        </p>

        <div className="grid grid-cols-2 gap-3 pt-1">
          {/* Light Mode Card */}
          <div 
            onClick={() => setTheme('light')}
            className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
              theme === 'light' 
                ? 'border-orange-500 bg-orange-500/5' 
                : 'border-neutral-200 dark:border-[#2b3140] hover:border-neutral-300'
            }`}
          >
            <div className="w-9 h-9 rounded-lg bg-neutral-100 text-neutral-800 flex items-center justify-center border border-neutral-200">
              <Sun className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 dark:text-neutral-100">Light Mode</div>
              <div className="text-[11px] text-neutral-500">Crisp daytime contrast</div>
            </div>
          </div>

          {/* Darker Grey Mode Card */}
          <div 
            onClick={() => setTheme('dark')}
            className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
              theme === 'dark' 
                ? 'border-orange-500 bg-orange-500/10' 
                : 'border-neutral-200 dark:border-[#2b3140] hover:border-neutral-300'
            }`}
          >
            <div className="w-9 h-9 rounded-lg bg-[#14161d] text-neutral-100 flex items-center justify-center border border-[#2b3140]">
              <Moon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 dark:text-neutral-100">Darker Grey Mode</div>
              <div className="text-[11px] text-neutral-400">Deep grey with orange accents</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI CV Model Parameters Card */}
      <div className="p-4 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] space-y-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          AI Detection & Vision Parameters
        </h3>

        {/* Confidence Threshold */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">
              YOLO Confidence Threshold
            </span>
            <span className="font-mono text-orange-500 font-bold">{confidenceThreshold}%</span>
          </div>
          <input 
            type="range" 
            min="30" 
            max="95" 
            value={confidenceThreshold}
            onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
            className="w-full h-1.5 bg-neutral-200 dark:bg-[#282e3b] rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <p className="text-[11px] text-neutral-500">
            Higher values reduce false positives in high occlusions and dense crowds.
          </p>
        </div>

        {/* Density Limit Trigger */}
        <div className="space-y-1.5 pt-2">
          <div className="flex justify-between text-xs">
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">
              Critical Density Alert Trigger
            </span>
            <span className="font-mono text-orange-500 font-bold">{densityLimit}%</span>
          </div>
          <input 
            type="range" 
            min="50" 
            max="100" 
            value={densityLimit}
            onChange={(e) => setDensityLimit(Number(e.target.value))}
            className="w-full h-1.5 bg-neutral-200 dark:bg-[#282e3b] rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <p className="text-[11px] text-neutral-500">
            Zones exceeding this capacity percentage will trigger audible alarms & operator notices.
          </p>
        </div>

        {/* Toggles */}
        <div className="pt-2 border-t border-neutral-200 dark:border-[#272d3c] space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-neutral-800 dark:text-neutral-200">
                GPU Hardware Acceleration (TensorRT)
              </div>
              <div className="text-[11px] text-neutral-500">
                Utilize local GPU inference pipelines for sub-20ms frame latency
              </div>
            </div>
            <input 
              type="checkbox" 
              checked={hardwareAcc} 
              onChange={(e) => setHardwareAcc(e.target.checked)}
              className="w-4 h-4 text-orange-500 accent-orange-500 cursor-pointer rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-neutral-800 dark:text-neutral-200">
                Sound Alert Chimes
              </div>
              <div className="text-[11px] text-neutral-500">
                Play acoustic chime when critical crowd threshold is violated
              </div>
            </div>
            <input 
              type="checkbox" 
              checked={soundAlerts} 
              onChange={(e) => setSoundAlerts(e.target.checked)}
              className="w-4 h-4 text-orange-500 accent-orange-500 cursor-pointer rounded"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
